import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

// Admin calls send the httpOnly session cookie.
export const adminApi = axios.create({ baseURL: API, withCredentials: true });

// AI concierge chat
export const sendChat = async (sessionId, message, lang) =>
  (await api.post("/chat", { session_id: sessionId, message, lang })).data;

// ---- Blog (public) ----
export const fetchBlogList = async () => (await api.get("/blog")).data;
export const fetchBlogPostBySlug = async (slug) => (await api.get(`/blog/${slug}`)).data;

// ---- Blog (admin) ----
export const adminGetBlog = async () => (await adminApi.get("/admin/blog")).data;
export const adminCreateBlog = async (p) => (await adminApi.post("/admin/blog", p)).data;
export const adminUpdateBlog = async (id, p) => (await adminApi.put(`/admin/blog/${id}`, p)).data;
export const adminDeleteBlog = async (id) => (await adminApi.delete(`/admin/blog/${id}`)).data;

// ---- AI Content Studio ----
export const adminContentCategories = async () =>
  (await adminApi.get("/admin/content/categories")).data;
export const adminContentIdeas = async (payload) =>
  (await adminApi.post("/admin/content/ideas", payload)).data;
// Polls a background content-studio job until it is done or errors out.
// Rejects with an axios-shaped error so ContentStudio's existing catch(e => e?.response?.data?.detail) keeps working.
const pollContentJob = async (jobId, { intervalMs = 3000, timeoutMs = 5 * 60 * 1000 } = {}) => {
  const start = Date.now();
  // Small first delay so we don't hammer the job just after start.
  await new Promise((r) => setTimeout(r, 800));
  while (Date.now() - start < timeoutMs) {
    const { data } = await adminApi.get(`/admin/content/jobs/${jobId}`);
    if (data.status === "done") return data.result;
    if (data.status === "error") {
      const err = new Error(data.error || "Content job failed");
      err.response = { status: data.error_status || 500, data: { detail: data.error || "Content job failed" } };
      throw err;
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  const err = new Error("Content job timed out");
  err.response = { status: 504, data: { detail: "Content job timed out. Please try again." } };
  throw err;
};

export const adminContentGenerate = async (payload) => {
  const { data } = await adminApi.post("/admin/content/generate/start", payload);
  return pollContentJob(data.job_id);
};
export const adminContentTranslate = async (payload) => {
  const { data } = await adminApi.post("/admin/content/translate/start", payload);
  return pollContentJob(data.job_id);
};
export const adminContentImage = async (payload) =>
  (await adminApi.post("/admin/content/image", payload, { timeout: 120000 })).data;
export const adminBlogFullCreate = async (payload) =>
  (await adminApi.post("/admin/blog/full", payload)).data;
export const adminBlogFullUpdate = async (id, payload) =>
  (await adminApi.put(`/admin/blog/full/${id}`, payload)).data;
export const adminBlogSetStatus = async (id, body) =>
  (await adminApi.patch(`/admin/blog/${id}/status`, body)).data;
export const adminIndexNowResubmit = async () =>
  (await adminApi.post("/admin/indexnow/resubmit-all")).data;
export const adminIndexNowResubmitRecent = async (days = 7) =>
  (await adminApi.post(`/admin/indexnow/resubmit-recent?days=${days}`)).data;

// Convert a relative /api/uploads/... URL from the backend into an absolute URL
// against REACT_APP_BACKEND_URL for use in <img src>.
export const resolveMediaUrl = (u) => {
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith("/")) return `${BACKEND_URL}${u}`;
  return u;
};

// Resolves a multilingual text field ({en,fr,tr}) to the active language.
export const localized = (field, lang = "en") => {
  if (field == null) return "";
  if (typeof field === "string") return field;
  return field[lang] ?? field.en ?? Object.values(field)[0] ?? "";
};

// Resolves a multilingual list field ({en:[],fr:[],tr:[]}) to an array.
export const localizedList = (field, lang = "en") => {
  if (Array.isArray(field)) return field;
  if (!field || typeof field !== "object") return [];
  return field[lang] ?? field.en ?? [];
};

// ---- Auth ----
export const postSession = async (sessionId) =>
  (await adminApi.post("/auth/session", null, { headers: { "X-Session-ID": sessionId } })).data;
export const getMe = async () => (await adminApi.get("/auth/me")).data;
export const logoutAdmin = async () => (await adminApi.post("/auth/logout")).data;

// ---- Admin: leads ----
export const adminGetLeads = async () => (await adminApi.get("/admin/leads")).data;
export const adminUpdateLead = async (id, status) =>
  (await adminApi.patch(`/admin/leads/${id}`, { status })).data;
export const adminDeleteLead = async (id) => (await adminApi.delete(`/admin/leads/${id}`)).data;

// ---- Admin: properties ----
export const adminGetProperties = async () => (await adminApi.get("/admin/properties")).data;
export const adminCreateProperty = async (p) => (await adminApi.post("/admin/properties", p)).data;
export const adminUpdateProperty = async (id, p) => (await adminApi.put(`/admin/properties/${id}`, p)).data;
export const adminDeleteProperty = async (id) => (await adminApi.delete(`/admin/properties/${id}`)).data;

export const fetchProperties = async (params = {}) => {
  const { data } = await api.get("/properties", { params });
  return data;
};

export const fetchProperty = async (id) => {
  const { data } = await api.get(`/properties/${id}`);
  return data;
};

export const submitLead = async (payload) => {
  const { data } = await api.post("/leads", payload);
  return data;
};

export const formatPrice = (value, unit = "EUR") => {
  const formatted = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: unit,
    maximumFractionDigits: 0,
  }).format(value);
  return formatted;
};

// Appends sizing/quality params to remote images to reduce payload and speed up load.
export const optimizeImg = (url, w = 1200) => {
  if (!url || typeof url !== "string") return url;
  const sep = url.includes("?") ? "&" : "?";
  if (url.includes("images.unsplash.com")) {
    return `${url}${sep}auto=format&fit=crop&w=${w}&q=70`;
  }
  if (url.includes("images.pexels.com")) {
    const base = url.split("?")[0];
    return `${base}?auto=compress&cs=tinysrgb&w=${w}`;
  }
  return url;
};
